import React, { useState, useEffect } from "react";
import { 
  Heart, 
  User,
  Bell,
  Smartphone,
  Shield,
  Save,
  Download,
  Lock,
  Activity
} from "lucide-react";
import { Link } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import { db } from "../../firebase/config";
import { ref, update } from "firebase/database";
import PatientSidebar from "../../components/PatientSidebar";
import SecurityModal from "../../components/SecurityModal";
import CaregiverManager from "./CaregiverManager";
import "./PatientSettings.css";

const PatientSettings = () => {
  const { currentUser, userData } = useAuth();
  const { showAlert } = useToast();
  const [profileData, setProfileData] = useState({
    name: "",
    email: "",
    phone: "",
    age: 0,
    bloodGroup: "Unknown",
    gender: "Not Specified",
    height: "",
    weight: "",
    allergies: "",
    chronicDiseases: "",
    emergencyContact: {
      name: "",
      relation: "",
      phone: ""
    },
    primaryDoctorId: "",
    healthIssue: ""
  });

  const [loading, setLoading] = useState(false);
  const [showSecurityModal, setShowSecurityModal] = useState(false);

  useEffect(() => {
    if (userData) {
      setProfileData({
        name: userData.name || "",
        email: userData.email || "",
        phone: userData.phone || "",
        age: userData.age || 0,
        bloodGroup: userData.bloodGroup || "Unknown",
        gender: userData.gender || "Not Specified",
        height: userData.height || "",
        weight: userData.weight || "",
        allergies: Array.isArray(userData.allergies) ? userData.allergies.join(", ") : userData.allergies || "",
        chronicDiseases: Array.isArray(userData.chronicDiseases) ? userData.chronicDiseases.join(", ") : userData.chronicDiseases || "",
        emergencyContact: userData.emergencyContact || { name: "", relation: "", phone: "" },
        primaryDoctorId: userData.primaryDoctorId || "",
        healthIssue: userData.healthIssue || ""
      });
    }
  }, [userData]);

  const [activeToggles, setActiveToggles] = useState({
    vitalAlerts: true,
    deviceAlerts: true,
    emailReports: false,
    autoSync: true,
    shareWithDoctor: true
  });

  const toggleSwitch = (key) => {
    setActiveToggles(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleSaveProfile = async () => {
    setLoading(true);
    try {
      const userRef = ref(db, `users/${currentUser.uid}`);
      
      const payload = {
        ...profileData,
        role: userData.role || "patient",
        status: userData.status || "active",
        emailVerified: userData.emailVerified ?? true,
        allergies: profileData.allergies.split(",").map(a => a.trim()).filter(a => a !== ""),
        chronicDiseases: profileData.chronicDiseases.split(",").map(d => d.trim()).filter(d => d !== ""),
        age: Number(profileData.age) || 0,
        height: profileData.height,
        weight: profileData.weight
      };

      await update(userRef, payload);
      showAlert("Patient profile synchronized successfully!", "success");
    } catch (error) {
      showAlert("Error updating profile: " + error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="dashboard-shell">
      <PatientSidebar />

      <main className="dashboard-content">
        <header className="settings-header">
          <div className="header-text">
            <h1>Settings</h1>
            <p>Manage your account and preferences</p>
          </div>
        </header>

        <div className="scrollable-content settings-grid">
          {/* Clinical Identity & Biometrics */}
          <div className="settings-card glass-card clinical-profile-card">
            <div className="card-header">
              <User size={18} className="icon-blue" />
              <h3>Clinical Identity & Biometrics</h3>
            </div>
            <div className="card-body">
              <div className="profile-form-grid">
                <div className="input-group">
                  <label>Full Name</label>
                  <input 
                    type="text" 
                    value={profileData.name} 
                    onChange={(e) => setProfileData({...profileData, name: e.target.value})}
                  />
                </div>
                <div className="input-group">
                  <label>Professional Email</label>
                  <input 
                    type="email" 
                    value={profileData.email} 
                    onChange={(e) => setProfileData({...profileData, email: e.target.value})}
                  />
                </div>
                <div className="input-group">
                  <label>Phone Number</label>
                  <input 
                    type="text" 
                    value={profileData.phone} 
                    onChange={(e) => setProfileData({...profileData, phone: e.target.value})}
                  />
                </div>
                <div className="input-group">
                  <label>Age</label>
                  <input 
                    type="number" 
                    value={profileData.age} 
                    onChange={(e) => setProfileData({...profileData, age: e.target.value})}
                  />
                </div>
                <div className="input-group">
                  <label>Gender</label>
                  <select 
                    value={profileData.gender} 
                    onChange={(e) => setProfileData({...profileData, gender: e.target.value})}
                    className="high-fidelity-select"
                  >
                    <option value="Not Specified">Not Specified</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div className="input-group">
                  <label>Blood Group</label>
                  <select 
                    value={profileData.bloodGroup} 
                    onChange={(e) => setProfileData({...profileData, bloodGroup: e.target.value})}
                    className="high-fidelity-select"
                  >
                    <option value="Unknown">Unknown</option>
                    <option value="A+">A+</option>
                    <option value="A-">A-</option>
                    <option value="B+">B+</option>
                    <option value="B-">B-</option>
                    <option value="O+">O+</option>
                    <option value="O-">O-</option>
                    <option value="AB+">AB+</option>
                    <option value="AB-">AB-</option>
                  </select>
                </div>
                <div className="input-group">
                  <label>Height (cm)</label>
                  <input 
                    type="text" 
                    value={profileData.height} 
                    onChange={(e) => setProfileData({...profileData, height: e.target.value})}
                  />
                </div>
                <div className="input-group">
                  <label>Weight (kg)</label>
                  <input 
                    type="text" 
                    value={profileData.weight} 
                    onChange={(e) => setProfileData({...profileData, weight: e.target.value})}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Medical Context Section */}
          <div className="settings-card glass-card clinical-context-card">
            <div className="card-header">
              <Activity size={18} className="icon-blue" />
              <h3>Medical Context</h3>
            </div>
            <div className="card-body">
              <div className="input-group full-width">
                <label>Current Health Issue (Chief Complaint)</label>
                <textarea 
                  value={profileData.healthIssue} 
                  placeholder="Describe your current medical concern..."
                  onChange={(e) => setProfileData({...profileData, healthIssue: e.target.value})}
                  className="high-fidelity-textarea"
                />
              </div>
              <div className="input-group full-width">
                <label>Allergies (Comma separated)</label>
                <input 
                  type="text" 
                  value={profileData.allergies} 
                  placeholder="e.g. Peanuts, Penicillin..."
                  onChange={(e) => setProfileData({...profileData, allergies: e.target.value})}
                />
              </div>
              <div className="input-group full-width">
                <label>Chronic Diseases (Comma separated)</label>
                <input 
                  type="text" 
                  value={profileData.chronicDiseases} 
                  placeholder="e.g. Type 2 Diabetes, Hypertension..."
                  onChange={(e) => setProfileData({...profileData, chronicDiseases: e.target.value})}
                />
              </div>

              {/* Emergency Contact Hub */}
              <div className="emergency-contact-hud mt-6">
                <div className="hud-label-row">
                  <strong>Emergency Contact Coordination</strong>
                </div>
                <div className="profile-form-grid mt-4">
                  <div className="input-group">
                    <label>Contact Name</label>
                    <input 
                      type="text" 
                      value={profileData.emergencyContact.name} 
                      onChange={(e) => setProfileData({
                        ...profileData, 
                        emergencyContact: { ...profileData.emergencyContact, name: e.target.value }
                      })}
                    />
                  </div>
                  <div className="input-group">
                    <label>Relationship</label>
                    <input 
                      type="text" 
                      value={profileData.emergencyContact.relation} 
                      onChange={(e) => setProfileData({
                        ...profileData, 
                        emergencyContact: { ...profileData.emergencyContact, relation: e.target.value }
                      })}
                    />
                  </div>
                  <div className="input-group full-width">
                    <label>Emergency Phone</label>
                    <input 
                      type="text" 
                      value={profileData.emergencyContact.phone} 
                      onChange={(e) => setProfileData({
                        ...profileData, 
                        emergencyContact: { ...profileData.emergencyContact, phone: e.target.value }
                      })}
                    />
                  </div>
                </div>
              </div>

              <button 
                className="premium-btn primary mt-8" 
                onClick={handleSaveProfile}
                disabled={loading}
              >
                <Save size={16} />
                {loading ? "Synchronizing Clinical Data..." : "Synchronize All Profiles"}
              </button>
            </div>
          </div>

          {/* Notifications Section */}
          <div className="settings-card glass-card">
            <div className="card-header">
              <Bell size={18} className="icon-blue" />
              <h3>Notifications</h3>
            </div>
            <div className="card-body toggle-list">
              <div className="toggle-item">
                <div className="toggle-info">
                  <strong>Vital Alerts</strong>
                  <span>Get notified when vitals are abnormal</span>
                </div>
                <div 
                  className={`switch ${activeToggles.vitalAlerts ? "on" : "off"}`}
                  onClick={() => toggleSwitch("vitalAlerts")}
                >
                  <div className="slider"></div>
                </div>
              </div>
              <div className="toggle-item">
                <div className="toggle-info">
                  <strong>Device Alerts</strong>
                  <span>Low battery & disconnection alerts</span>
                </div>
                <div 
                  className={`switch ${activeToggles.deviceAlerts ? "on" : "off"}`}
                  onClick={() => toggleSwitch("deviceAlerts")}
                >
                  <div className="slider"></div>
                </div>
              </div>
              <div className="toggle-item">
                <div className="toggle-info">
                  <strong>Email Reports</strong>
                  <span>Weekly health summary via email</span>
                </div>
                <div 
                  className={`switch ${activeToggles.emailReports ? "on" : "off"}`}
                  onClick={() => toggleSwitch("emailReports")}
                >
                  <div className="slider"></div>
                </div>
              </div>
            </div>
          </div>

          {/* Device Section */}
          <div className="settings-card glass-card">
            <div className="card-header">
              <Smartphone size={18} className="icon-blue" />
              <h3>Device</h3>
            </div>
            <div className="card-body toggle-list">
              <div className="toggle-item">
                <div className="toggle-info">
                  <strong>Auto-sync</strong>
                  <span>Sync vitals automatically</span>
                </div>
                <div 
                  className={`switch ${activeToggles.autoSync ? "on" : "off"}`}
                  onClick={() => toggleSwitch("autoSync")}
                >
                  <div className="slider"></div>
                </div>
              </div>
              <div className="setting-row">
                <div className="row-info">
                  <strong>Sync Interval</strong>
                  <span>Every 30 seconds</span>
                </div>
                <button className="action-link">Change</button>
              </div>
            </div>
          </div>

          {/* Privacy Section */}
          <div className="settings-card glass-card">
            <div className="card-header">
              <Shield size={18} className="icon-blue" />
              <h3>Privacy</h3>
            </div>
            <div className="card-body toggle-list">
              <div className="toggle-item">
                <div className="toggle-info">
                  <strong>Share with Doctor</strong>
                  <span>Allow your doctor to view vitals</span>
                </div>
                <div 
                  className={`switch ${activeToggles.shareWithDoctor ? "on" : "off"}`}
                  onClick={() => toggleSwitch("shareWithDoctor")}
                >
                  <div className="slider"></div>
                </div>
              </div>
              <div className="setting-row">
                <div className="row-info">
                  <strong>Data Export</strong>
                  <span>Download your health data</span>
                </div>
                <button className="premium-btn secondary">
                  <Download size={16} />
                  Export
                </button>
              </div>
            </div>
          </div>

          {/* Security Section */}
          <div className="settings-card glass-card">
            <div className="card-header">
              <Lock size={18} className="icon-blue" />
              <h3>Security</h3>
            </div>
            <div className="card-body">
              <div className="security-summary">
                <p>Manage your clinical access and security keys.</p>
              </div>
              <button 
                className="premium-btn primary w-full mt-4" 
                onClick={() => setShowSecurityModal(true)}
              >
                <Lock size={16} />
                Change Password
              </button>
            </div>
          </div>

          {/* Caregiver Network HUD */}
          <CaregiverManager />
        </div>
      </main>

      <SecurityModal 
        isOpen={showSecurityModal} 
        onClose={() => setShowSecurityModal(false)} 
      />
    </div>
  );
};

export default PatientSettings;
